package dev.kuml.portal.web.editor

/**
 * Pure `Set<String>` roster of currently-present collaborators, keyed by the hex-encoded
 * connection token [CollabFrame.decodePresence] decodes -- DOM-free so the add/remove bookkeeping
 * is directly unit-testable (see `CollabRosterTest`) independently of `CollabClient.kt`'s
 * WebSocket glue. No cursor/selection data, no schema beyond "this token is currently present" --
 * matches the plan's v1 scope (a trivial liveness roster, not real awareness data).
 */
public object CollabRoster {
    /** Adds [tokenHex] to [roster] -- idempotent, a token already present is a no-op. */
    public fun withPresent(
        roster: Set<String>,
        tokenHex: String,
    ): Set<String> = roster + tokenHex

    /** Removes [tokenHex] from [roster] -- idempotent, a token already absent is a no-op. */
    public fun withAbsent(
        roster: Set<String>,
        tokenHex: String,
    ): Set<String> = roster - tokenHex

    /**
     * Merges [event] into [identities] (R-16, keyed by `tokenHex` like [roster] above). A blank
     * [CollabIdentityEvent.displayName] (the snapshot wire shape, see [CollabFrame]'s KDoc) never
     * OVERWRITES an already-known non-blank name for the same token -- an announce frame (which
     * DOES carry a name) can arrive before or after the snapshot frame for the same peer depending
     * on connection timing, and a snapshot frame must never blank out a name an announce already
     * supplied.
     */
    public fun withIdentity(
        identities: Map<String, CollabPeer>,
        event: CollabIdentityEvent,
    ): Map<String, CollabPeer> {
        val existing = identities[event.tokenHex]
        val displayName = if (event.displayName.isNotEmpty()) event.displayName else existing?.displayName ?: ""
        return identities + (event.tokenHex to CollabPeer(event.tokenHex, event.userId, displayName))
    }

    /** Removes [tokenHex]'s identity -- called alongside [withAbsent] when a peer disconnects. */
    public fun withoutIdentity(
        identities: Map<String, CollabPeer>,
        tokenHex: String,
    ): Map<String, CollabPeer> = identities - tokenHex

    /**
     * The peers currently in [roster] that also have a known identity, stably sorted by
     * `tokenHex` -- so the rendered avatar order never reshuffles on every single presence/identity
     * frame (only membership changes move avatars around). A roster entry with no matching
     * identity yet (identity frames can arrive slightly after the corresponding presence frame) is
     * simply omitted until its identity arrives.
     */
    public fun peersOf(
        roster: Set<String>,
        identities: Map<String, CollabPeer>,
    ): List<CollabPeer> = roster.mapNotNull { identities[it] }.sortedBy { it.tokenHex }

    /**
     * Every token in [roster], stably sorted -- used by `CollabClient.handleUnintendedClose`'s
     * `GaveUp` branch when [CollabReconnect] gives up on reconnection for good. A real per-peer
     * disconnect always reaches a caller as a `present = false` presence-changed callback, which is
     * the ONLY path that tears down a caller's per-peer presence/selection bookkeeping (e.g.
     * `EditorScreen.kt`'s `presenceState`, whose [PeerPresence.selection] deliberately has NO TTL of
     * its own -- see [PresenceState.CURSOR_TTL_MS]'s KDoc). Giving up is not itself a per-peer
     * event, but from that bookkeeping's point of view it must look like one for every roster entry
     * still known at the moment of giving up -- otherwise a peer's last-known selection stays stuck
     * forever behind an avatar strip that has already gone to zero. Sorted (not just [roster]'s own
     * iteration order) purely so callers of this function get a deterministic, easily asserted
     * result -- the actual order the resulting synthetic `present = false` events fire in has no
     * behavioural effect, since each is an independent per-token removal.
     */
    public fun tokensToReleaseOnGiveUp(roster: Set<String>): List<String> = roster.sorted()
}

/** One collaborator's identity as decoded off the wire (R-16) -- see [CollabFrame.decodeIdentity]. */
public data class CollabPeer(
    val tokenHex: String,
    val userId: String,
    val displayName: String,
)
